import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from '@mui/material';
import { ArrowBack, Edit, Refresh, Add } from '@mui/icons-material';
import { DataGridPremium, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-premium';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { format, subDays } from 'date-fns';
import { campaignsService, UpdateCampaignRequest } from '../../services/campaigns.service';
import { adsetsService, AdSet } from '../../services/adsets.service';
import { insightsService } from '../../services/insights.service';
import StatusBadge from '../../components/StatusBadge';
import { HistoryButton } from '../../components/EntityHistory';

export default function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState<UpdateCampaignRequest>({});
  const [insightsDateRange] = useState({
    date_from: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    date_to: format(new Date(), 'yyyy-MM-dd'),
  });

  const { data: campaign, isLoading } = useQuery({
    queryKey: ['campaign', id],
    queryFn: () => campaignsService.getById(id!),
    enabled: !!id,
  });

  const { data: adsetsResult, isLoading: adsetsLoading } = useQuery({
    queryKey: ['campaign-adsets', id],
    queryFn: () => adsetsService.query({ campaign_id: id }),
    enabled: !!id && tab === 0,
  });

  const { data: insights, isLoading: insightsLoading, refetch: refetchInsights } = useQuery({
    queryKey: ['campaign-insights', id, insightsDateRange],
    queryFn: () => insightsService.getCampaignInsights(id!, insightsDateRange),
    enabled: !!id && tab === 1,
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateCampaignRequest) => campaignsService.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', id] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      setEditDialogOpen(false);
    },
  });



  const adSetColumns: GridColDef<AdSet>[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <StatusBadge
          status={params.value}
          effectiveStatus={params.row.effective_status}
          staleSince={params.row.status_stale_since}
        />
      ),
    },
    {
      field: 'optimization_goal',
      headerName: 'Optimization',
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Chip label={params.value} size="small" variant="outlined" />
      ),
    },
    {
      field: 'daily_budget',
      headerName: 'Daily Budget',
      width: 120,
      valueFormatter: (value) =>
        value ? `$${Number(value).toFixed(2)}` : '-',
    },
    {
      field: 'lifetime_budget',
      headerName: 'Lifetime Budget',
      width: 130,
      valueFormatter: (value) =>
        value ? `$${Number(value).toFixed(2)}` : '-',
    },
    {
      field: 'start_time',
      headerName: 'Start',
      width: 110,
      valueFormatter: (value) =>
        value ? new Date(value).toLocaleDateString() : '-',
    },
    {
      field: 'end_time',
      headerName: 'End',
      width: 110,
      valueFormatter: (value) =>
        value ? new Date(value).toLocaleDateString() : '-',
    },
  ];

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width={300} height={50} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/campaigns')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" fontWeight={600} sx={{ flexGrow: 1 }}>
          {campaign?.name}
        </Typography>
        <Box sx={{ mr: 2 }}>
          <StatusBadge
            status={campaign?.status || 'UNKNOWN'}
            effectiveStatus={campaign?.effective_status}
            staleSince={campaign?.status_stale_since}
          />
          {campaign && (
            <HistoryButton entityType="campaigns" entityId={campaign.id} entityName={campaign.name} />
          )}
        </Box>
        <Button variant="contained" startIcon={<Edit />} onClick={() => navigate(`/campaigns/${id}/edit`)}>
          Edit Campaign
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Ad Account
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {campaign?.ad_account?.name || '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Objective
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {campaign?.objective}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Daily Budget
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {campaign?.daily_budget
                  ? `$${Number(campaign.daily_budget).toFixed(2)}`
                  : '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Lifetime Budget
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {campaign?.lifetime_budget
                  ? `$${Number(campaign.lifetime_budget).toFixed(2)}`
                  : '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Created
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {campaign?.created_at
                  ? new Date(campaign.created_at).toLocaleDateString()
                  : '-'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Ad Sets" />
        <Tab label="Insights" />
        <Tab label="Settings" />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate(`/adsets/new?campaign_id=${id}`)}
            >
              Create Ad Set
            </Button>
          </Box>
          {adsetsResult?.data.length === 0 && !adsetsLoading ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" gutterBottom>
                  No Ad Sets Yet
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  Create your first ad set to start running ads in this campaign.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => navigate(`/adsets/new?campaign_id=${id}`)}
                >
                  Create Ad Set
                </Button>
              </CardContent>
            </Card>
          ) : (
            <DataGridPremium
              rows={adsetsResult?.data || []}
              columns={adSetColumns}
              loading={adsetsLoading}
              pageSizeOptions={[50, 100, 200, 400]}
              initialState={{
                pagination: { paginationModel: { pageSize: 100 } },
              }}
              onRowClick={(params: GridRowParams) => navigate(`/adsets/${params.row.id}`)}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 2,
                cursor: 'pointer',
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiDataGrid-row:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            />
          )}
        </Box>
      )}

      {tab === 1 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Performance Metrics ({insightsDateRange.date_from} to {insightsDateRange.date_to})
              </Typography>
              <Button
                startIcon={<Refresh />}
                onClick={() => refetchInsights()}
                disabled={insightsLoading}
              >
                Refresh
              </Button>
            </Box>
            {insightsLoading ? (
              <Grid container spacing={3}>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                    <Skeleton variant="rectangular" height={100} />
                  </Grid>
                ))}
              </Grid>
            ) : insights && insights.length > 0 ? (
              <>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          Impressions
                        </Typography>
                        <Typography variant="h5" fontWeight={600}>
                          {insights.reduce((sum, i) => sum + (i.impressions || 0), 0).toLocaleString()}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          Clicks
                        </Typography>
                        <Typography variant="h5" fontWeight={600}>
                          {insights.reduce((sum, i) => sum + (i.clicks || 0), 0).toLocaleString()}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          Spend
                        </Typography>
                        <Typography variant="h5" fontWeight={600}>
                          ${(insights.reduce((sum, i) => sum + (i.spend || 0), 0) / 100).toFixed(2)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          Reach
                        </Typography>
                        <Typography variant="h5" fontWeight={600}>
                          {insights.reduce((sum, i) => sum + (i.reach || 0), 0).toLocaleString()}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          CTR
                        </Typography>
                        <Typography variant="h5" fontWeight={600}>
                          {(() => {
                            const totalImpressions = insights.reduce((sum, i) => sum + (i.impressions || 0), 0);
                            const totalClicks = insights.reduce((sum, i) => sum + (i.clicks || 0), 0);
                            return totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : '0.00';
                          })()}%
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="body2" color="text.secondary">
                          CPC
                        </Typography>
                        <Typography variant="h5" fontWeight={600}>
                          ${(() => {
                            const totalSpend = insights.reduce((sum, i) => sum + (i.spend || 0), 0);
                            const totalClicks = insights.reduce((sum, i) => sum + (i.clicks || 0), 0);
                            return totalClicks > 0 ? ((totalSpend / 100) / totalClicks).toFixed(2) : '0.00';
                          })()}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
                <Typography variant="subtitle2" sx={{ mt: 3, mb: 2 }}>
                  Daily Breakdown
                </Typography>
                <Box sx={{ overflowX: 'auto' }}>
                  <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                    <Box component="thead">
                      <Box component="tr" sx={{ borderBottom: '2px solid', borderColor: 'divider' }}>
                        <Box component="th" sx={{ p: 1, textAlign: 'left' }}>Date</Box>
                        <Box component="th" sx={{ p: 1, textAlign: 'right' }}>Impressions</Box>
                        <Box component="th" sx={{ p: 1, textAlign: 'right' }}>Clicks</Box>
                        <Box component="th" sx={{ p: 1, textAlign: 'right' }}>Spend</Box>
                        <Box component="th" sx={{ p: 1, textAlign: 'right' }}>CTR</Box>
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {insights.map((insight) => (
                        <Box
                          component="tr"
                          key={insight.date}
                          sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
                        >
                          <Box component="td" sx={{ p: 1 }}>{insight.date}</Box>
                          <Box component="td" sx={{ p: 1, textAlign: 'right' }}>
                            {(insight.impressions || 0).toLocaleString()}
                          </Box>
                          <Box component="td" sx={{ p: 1, textAlign: 'right' }}>
                            {(insight.clicks || 0).toLocaleString()}
                          </Box>
                          <Box component="td" sx={{ p: 1, textAlign: 'right' }}>
                            ${((insight.spend || 0) / 100).toFixed(2)}
                          </Box>
                          <Box component="td" sx={{ p: 1, textAlign: 'right' }}>
                            {insight.ctr?.toFixed(2) || '0.00'}%
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </>
            ) : (
              <Typography color="text.secondary">
                No insights data available for this campaign. Sync data from your ad account to see metrics.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 2 && (
        <Card>
          <CardContent>
            <Typography color="text.secondary">
              Campaign settings will be displayed here.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Edit Campaign Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Campaign</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Campaign Name"
              fullWidth
              value={editData.name || ''}
              onChange={(e) => setEditData({ ...editData, name: e.target.value })}
            />
            <FormControl fullWidth>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Status
              </Typography>
              <ToggleButtonGroup
                value={editData.status}
                exclusive
                onChange={(_, value) => value && setEditData({ ...editData, status: value })}
                fullWidth
              >
                <ToggleButton value="PAUSED">Paused</ToggleButton>
                <ToggleButton value="ACTIVE">Active</ToggleButton>
              </ToggleButtonGroup>
            </FormControl>
            <TextField
              label="Daily Budget"
              type="number"
              fullWidth
              value={editData.daily_budget || ''}
              onChange={(e) => setEditData({
                ...editData,
                daily_budget: e.target.value ? parseFloat(e.target.value) : undefined,
              })}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              helperText="Leave empty to use ad set budgets"
            />
            <TextField
              label="Lifetime Budget"
              type="number"
              fullWidth
              value={editData.lifetime_budget || ''}
              onChange={(e) => setEditData({
                ...editData,
                lifetime_budget: e.target.value ? parseFloat(e.target.value) : undefined,
              })}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              helperText="Total budget for campaign lifetime"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => updateMutation.mutate(editData)}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
